import { TaskCard } from "shared/datamodel/schemas";
import { TaskElement } from "shared/datamodel/schemas/task";
import { MappingTaskCardsToMonday } from "./gantt-mapping-hooks/use-mapping-settings-task-cards";
import { addHours, startOfDay } from "date-fns";
import { MondayColumn } from "shared/integrations/mapper";

export type TaskLikeElement = TaskCard | TaskElement;

export function ensureFullIdForTag(id: string): string {
  return id.startsWith("cMetadata-Tag-") ? id : `cMetadata-Tag-${id}`;
}

export interface DataInCards {
  haveAssignees: boolean;
  haveTags: boolean;
  hasSingleDate: boolean;
  hasTimeRange: boolean;
}

export function analyzeDataInTaskCards(nodes: Iterable<any>): DataInCards {
  let haveAssignees = false,
    haveTags = false,
    hasDate = false,
    hasTimeRange = false;

  for (const n of nodes) {
    const element = n.attrs.element as TaskLikeElement;
    haveAssignees ||= (element.assignees?.length ?? 0) > 0;
    haveTags ||= (element.tags?.length ?? 0) > 0;
    // TaskCard has dueDate, TaskElement has fromDate/toDate
    const dueDate = "dueDate" in element ? element.dueDate : undefined;
    hasDate ||= !!(element.fromDate || element.toDate || dueDate);
    hasTimeRange ||= element.fromDate != element.toDate;
  }

  return { haveAssignees, haveTags, hasSingleDate: hasDate && !hasTimeRange, hasTimeRange };
}

export function analyzeOptions(columns: Record<string, MondayColumn[]>) {
  return {
    assignee: columns["people"]?.length,
    tags: columns["dropdown"]?.length,
    singleDate: columns["timeline"]?.length || columns["date"]?.length,
    timeRange: columns["timeline"]?.length || (columns["date"]?.length ?? 0) >= 2,
    dependency: columns["dependency"]?.length,
  };
}

//@deprecated
export function getDataLossWarnings(dataInCards: DataInCards, options: Record<string, any>): string[] {
  const warnings: string[] = [];

  if (dataInCards.haveTags && (!options.tags || options.tags.length === 0)) {
    warnings.push("Tags - Data will be lost");
  }

  if (dataInCards.haveAssignees && (!options.assignee || options.assignee.length === 0)) {
    warnings.push("Assignee - Data will be lost");
  }

  if (dataInCards.hasSingleDate && (!options.date || options.date.length === 0)) {
    warnings.push("Date - Data will be lost");
  }

  if (dataInCards.hasTimeRange) {
    const hasTimelineOption = options.timeline && options.timeline.length > 0;
    const hasStartEndOptions = options.date && options.start_date.length >= 2;

    if (!hasTimelineOption && !hasStartEndOptions) {
      warnings.push("Date range - Data will be lost");
    }
  }

  return warnings;
}

//@deprecated
export function checkIsMissingMapping(
  mapping: Partial<MappingTaskCardsToMonday> | undefined,
  dateTab: string,
  dataInCards: DataInCards
): boolean {
  return (
    !mapping ||
    (dataInCards.haveAssignees && !mapping.assignee) ||
    (dataInCards.haveTags && !mapping.tags) ||
    (dataInCards.hasSingleDate && !mapping.date) ||
    (dataInCards.hasTimeRange &&
      (!dateTab ||
        (dateTab == "timeline" && !mapping.timeline) ||
        (dateTab == "start-end" && !(mapping.start_date && mapping.end_date))))
  );
}

function midday(timestamp: number): number {
  return addHours(startOfDay(timestamp), 12).valueOf();
}

export function prepareTaskCardsForMondayIntegration(
  cards: TaskLikeElement[],
  mapping: Partial<MappingTaskCardsToMonday>,
  dateTab: string,
  getTagName: (id: string) => string
) {
  return cards.map((card) => {
    const fields = {} as Record<string, any>;
    // TaskCard has dueDate, TaskElement only has fromDate/toDate
    const dueDate = "dueDate" in card ? card.dueDate : undefined;
    const toDate = dueDate || card.toDate;
    const fromDate = card.fromDate || toDate;
    const anyDate = card.toDate || card.fromDate;

    if (mapping["assignee"] && card.assignees?.length) {
      fields[mapping["assignee"]] = String(card.assignees[0].id);
    }
    if (mapping["tags"] && card.tags?.length) {
      fields[mapping["tags"]] = card.tags.map(getTagName).filter(Boolean); // Prevent mapping deleted tags
    }
    if (dateTab == "start-end") {
      if (mapping["start_date"] && fromDate) {
        fields[mapping["start_date"]] = midday(fromDate);
      }
      if (mapping["end_date"] && toDate) {
        fields[mapping["end_date"]] = midday(toDate);
      }
    } else if (dateTab == "timeline") {
      if (fromDate && toDate && mapping["timeline"]) {
        fields[mapping["timeline"]] = {
          fromDate: midday(fromDate),
          toDate: midday(toDate),
        };
      }
    } else if (mapping["date"] && anyDate) {
      fields[mapping["date"]] = midday(anyDate);
    }
    let description = card.description?.trim() ?? "";
    return {
      title: card.title,
      updates: description ? [description] : undefined,
      fields,
    };
  });
}
